/**
 * ops2026 — Attack path decisions (ADR-0251).
 *
 * The Mon/Thu `framework-watch` job proposes candidate attack paths;
 * the server-side judgement panel decides approve / reject / pending
 * and the result ships inside the next `ppath/v3` envelope. This view
 * surfaces every decision + the three per-candidate verdicts, with
 * owner-only Approve (promote a rejected / pending row) and Remove
 * (demote an approved row). Both overrides bump the envelope's
 * revision and write `staff_audit_log`.
 */

import { CheckCircle2, Clock3, Trash2, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  AdminAttackPathDecision,
  AdminAttackPathVerdicts,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useAuth } from '../../app/auth-context.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { hasRole } from '../../lib/roles.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

type Phase = 'loading' | 'ready' | 'error';
type StatusFilter = 'all' | 'approved' | 'rejected' | 'pending' | 'removed';

export function AttackPathDecisionsView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const auth = useAuth();
  const role = auth.state.kind === 'authenticated' ? auth.state.staff.role : undefined;
  const canOverride = hasRole(role, 'owner');

  const [phase, setPhase] = useState<Phase>('loading');
  const [decisions, setDecisions] = useState<readonly AdminAttackPathDecision[]>([]);
  const [revision, setRevision] = useState<number>(0);
  const [autoApprove, setAutoApprove] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setPhase('loading');
    setError(null);
    try {
      const res = await api.getAttackPathDecisions(
        filter === 'all' ? undefined : filter,
      );
      setDecisions(res.decisions);
      setRevision(res.revision);
      setAutoApprove(res.auto_approve);
      setPhase('ready');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load decisions.');
      setPhase('error');
    }
  }, [api, filter]);

  useEffect(() => { void load(); }, [load]);

  const onApprove = useCallback(async (id: string): Promise<void> => {
    if (!canOverride) return;
    setBusy(id);
    setNotice(null);
    try {
      await api.approveAttackPathDecision(id);
      setNotice({ tone: 'ok', text: 'Approved.' });
      await load();
    } catch (err) {
      setNotice({ tone: 'bad', text: err instanceof ApiError ? err.message : 'Approve failed.' });
    } finally {
      setBusy(null);
    }
  }, [api, canOverride, load]);

  const onRemove = useCallback(async (id: string): Promise<void> => {
    if (!canOverride) return;
    setBusy(id);
    setNotice(null);
    try {
      await api.removeAttackPathDecision(id);
      setNotice({ tone: 'ok', text: 'Removed.' });
      await load();
    } catch (err) {
      setNotice({ tone: 'bad', text: err instanceof ApiError ? err.message : 'Remove failed.' });
    } finally {
      setBusy(null);
    }
  }, [api, canOverride, load]);

  const summary = useMemo(() => {
    const counts: Record<string, number> = { approved: 0, rejected: 0, pending: 0, removed: 0 };
    for (const d of decisions) counts[d.status] = (counts[d.status] ?? 0) + 1;
    return counts;
  }, [decisions]);

  return (
    <div style={{ fontFamily: sansFont, color: C.text, padding: '32px 40px' }}>
      <h1 style={{ fontFamily: serifFont, fontSize: 32, margin: 0, marginBottom: 4 }}>
        Attack path decisions
      </h1>
      <p style={{ color: C.muted, margin: 0, marginBottom: 24, fontSize: 14 }}>
        ADR-0251 — the Mon/Thu panel decides which paths join the fleet-wide refusal list.
        Current catalog revision <strong>{revision}</strong>. Auto-approve is{' '}
        <strong style={{ color: autoApprove ? C.lime : C.coral }}>
          {autoApprove ? 'ON' : 'OFF'}
        </strong>
        . Admins can read; owners can override.
      </p>

      {notice !== null && (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            border: `1px solid ${notice.tone === 'ok' ? C.lime : C.coral}`,
            padding: '8px 12px',
            marginBottom: 16,
            background: notice.tone === 'ok' ? `${C.lime}18` : `${C.coral}18`,
          }}
        >
          {notice.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {(['all', 'approved', 'rejected', 'pending', 'removed'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              padding: '4px 10px',
              border: `1px solid ${filter === s ? C.text : C.border}`,
              background: filter === s ? C.surface : 'transparent',
              color: C.text,
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {s} {s === 'all' ? `· ${decisions.length}` : `· ${summary[s] ?? 0}`}
          </button>
        ))}
      </div>

      {phase === 'loading' && (
        <Card>
          <div style={{ padding: 24, color: C.muted, fontSize: 14 }}>Loading…</div>
        </Card>
      )}
      {phase === 'error' && (
        <Card>
          <div style={{ padding: 24, color: C.coral, fontSize: 14 }}>{error}</div>
        </Card>
      )}
      {phase === 'ready' && decisions.length === 0 && (
        <Card>
          <div style={{ padding: 24, color: C.muted, fontSize: 14 }}>
            No decisions yet. Candidates appear here when the Mon/Thu job POSTs.
          </div>
        </Card>
      )}
      {phase === 'ready' && decisions.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          {decisions.map((d) => (
            <DecisionRow
              key={d.addition_id}
              decision={d}
              canOverride={canOverride}
              busy={busy === d.addition_id}
              onApprove={() => void onApprove(d.addition_id)}
              onRemove={() => void onRemove(d.addition_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function statusIcon(status: AdminAttackPathDecision['status']): JSX.Element {
  if (status === 'approved') return <CheckCircle2 size={14} />;
  if (status === 'rejected') return <XCircle size={14} />;
  if (status === 'pending') return <Clock3 size={14} />;
  return <Trash2 size={14} />;
}

function statusTone(status: AdminAttackPathDecision['status']): 'ok' | 'bad' | 'warn' | 'muted' {
  if (status === 'approved') return 'ok';
  if (status === 'rejected') return 'bad';
  if (status === 'pending') return 'warn';
  return 'muted';
}

function DecisionRow(props: {
  readonly decision: AdminAttackPathDecision;
  readonly canOverride: boolean;
  readonly busy: boolean;
  readonly onApprove: () => void;
  readonly onRemove: () => void;
}): JSX.Element {
  const C = useC();
  const d = props.decision;
  const tone = statusTone(d.status);
  const toneColor =
    tone === 'ok' ? C.lime :
    tone === 'bad' ? C.coral :
    tone === 'warn' ? C.amber :
    C.muted;
  return (
    <Card>
      <div style={{ padding: 16, display: 'grid', gap: 10, fontFamily: sansFont }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ color: toneColor, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {statusIcon(d.status)}
            <strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: monoFont }}>
              {d.status}
            </strong>
          </span>
          <code style={{ fontFamily: monoFont, fontSize: 13, background: C.surface, padding: '2px 6px' }}>
            {d.path}
          </code>
          <Pill>{d.kind}</Pill>
          <Pill>{d.category}</Pill>
          <span style={{ fontSize: 11, color: C.muted, fontFamily: monoFont, marginLeft: 'auto' }}>
            {new Date(d.decided_at).toLocaleString()}
          </span>
        </div>
        <VerdictsBlock verdicts={d.verdicts} />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 11, color: C.muted, fontFamily: monoFont }}>
          <span>
            sources:{' '}
            {d.sources.map((s) => s.source).join(', ') || '—'}
          </span>
          <span>license: {d.license}</span>
          <span>decided_by: {d.decided_by ?? '—'}</span>
          {d.removed_at !== null && (
            <span>removed_at: {new Date(d.removed_at).toLocaleString()}</span>
          )}
        </div>
        {props.canOverride && (
          <div style={{ display: 'flex', gap: 8 }}>
            {d.status !== 'approved' && (
              <button
                type="button"
                onClick={props.onApprove}
                disabled={props.busy}
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  padding: '6px 12px',
                  border: `1px solid ${C.lime}`,
                  background: props.busy ? C.surface : `${C.lime}18`,
                  color: C.text,
                  cursor: props.busy ? 'wait' : 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Approve override
              </button>
            )}
            {d.status === 'approved' && (
              <button
                type="button"
                onClick={props.onRemove}
                disabled={props.busy}
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  padding: '6px 12px',
                  border: `1px solid ${C.coral}`,
                  background: props.busy ? C.surface : `${C.coral}18`,
                  color: C.text,
                  cursor: props.busy ? 'wait' : 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function VerdictsBlock(props: { readonly verdicts: AdminAttackPathVerdicts | null }): JSX.Element {
  const C = useC();
  const v = props.verdicts;
  if (v === null) {
    return <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>no verdicts</div>;
  }
  return (
    <div style={{ display: 'grid', gap: 4, fontFamily: monoFont, fontSize: 11, color: C.muted }}>
      <div><strong style={{ color: C.text }}>rules</strong>: {renderRules(v.rules)}</div>
      <div><strong style={{ color: C.text }}>traffic</strong>: {renderTraffic(v.traffic)}</div>
      <div><strong style={{ color: C.text }}>model</strong>: {renderModel(v.model)}</div>
      {v.overall_reason !== null && (
        <div style={{ color: C.coral }}>overall: {v.overall_reason}</div>
      )}
    </div>
  );
}

function renderRules(r: AdminAttackPathVerdicts['rules']): string {
  if (r === null) return 'skipped';
  if (r.verdict === 'approve') return 'approve';
  return `reject (${r.reason}${'detail' in r && r.detail ? ':' + r.detail : ''})`;
}
function renderTraffic(t: AdminAttackPathVerdicts['traffic']): string {
  if (t === null) return 'skipped';
  if (t.verdict === 'approve') {
    return `approve (crawler=${t.verified_crawler_sites}, humans=${t.human_visitor_sites})`;
  }
  return `reject (${t.reason}, ${t.site_count} site(s))`;
}
function renderModel(m: AdminAttackPathVerdicts['model']): string {
  if (m === null) return 'skipped';
  return `${m.verdict} — ${m.reason}`;
}
